/**
 * <salt-slider>
 *
 * One-slide-visible carousel with prev/next + pagination dots + swipe.
 * Used by how-to-use, science-slider.
 *
 * Markup:
 *   <salt-slider>
 *     <div data-role="track">
 *       <div data-role="slide">...</div>
 *       <div data-role="slide">...</div>
 *     </div>
 *     <button data-role="prev">…</button>
 *     <button data-role="next">…</button>
 *     <div data-role="dots"></div>
 *   </salt-slider>
 *
 * Init is deferred to the next animation frame so children are guaranteed
 * to be parsed/attached before we query for them. A `data-slider-state`
 * attribute records what happened ('ready' | 'no-slides' | 'no-track') for
 * easy DevTools diagnosis.
 */
(() => {
  if (customElements.get('salt-slider')) return;

  class SaltSlider extends HTMLElement {
    connectedCallback() {
      // Defer to next frame so the closing </salt-slider> tag and all
      // children are fully attached even in edge-case parse orders.
      if (this._scheduled) return;
      this._scheduled = true;
      requestAnimationFrame(() => this._init());
    }

    _init() {
      if (this._initialized) return;

      this.track = this.querySelector('[data-role="track"]');
      if (!this.track) {
        this.setAttribute('data-slider-state', 'no-track');
        // Retry once after a tick — section may still be hydrating.
        if (!this._retried) {
          this._retried = true;
          this._scheduled = false;
          requestAnimationFrame(() => this._init());
        }
        return;
      }

      this.slides = Array.from(this.track.querySelectorAll('[data-role="slide"]'));
      // Sliders with per-slide controls (science-slider) render one prev/next
      // pair inside every body card; sliders with shared controls (how-to-use)
      // render one pair at the slider root. Collect all of them so clicking
      // any visible arrow advances the carousel regardless of which slide
      // the user is on.
      this.prevBtns = Array.from(this.querySelectorAll('[data-role="prev"]'));
      this.nextBtns = Array.from(this.querySelectorAll('[data-role="next"]'));
      // Support one shared dots wrap (legacy) OR one dots wrap per slide
      // (current science-slider layout, where dots live inside each white
      // body card). When per-slide, we render the full N-dot set in every
      // wrap so the visible slide always has its own working pagination.
      this.dotsWraps = Array.from(this.querySelectorAll('[data-role="dots"]'));
      this.currentIndex = 0;

      if (this.slides.length === 0) {
        this.setAttribute('data-slider-state', 'no-slides');
        if (!this._retried) {
          this._retried = true;
          this._scheduled = false;
          requestAnimationFrame(() => this._init());
        }
        return;
      }

      this._initialized = true;
      this.setAttribute('data-slider-state', 'ready');
      this.setAttribute('data-slider-count', String(this.slides.length));

      this.buildDots();
      this.bindEvents();
      this.go(0);
      this._observePreload();
    }

    /**
     * Preload EVERY slide's images as soon as the slider nears the viewport,
     * so hitting prev/next switches cards instantly instead of kicking off a
     * fresh fetch on click (the reported ~1.5s stall when the next card's
     * photo wasn't cached yet).
     *
     * Slide images ship with `loading="lazy"` so they don't compete with
     * above-the-fold resources on first paint. But inactive slides are
     * translated off-screen inside an `overflow: clip` viewport, so the
     * browser keeps deferring them until the user actually switches — which
     * IS the stall. Once the section is ~600px from entering view we flip
     * those images to eager, giving them a head start while the user is still
     * reading the active card. Falls back to an immediate preload where
     * IntersectionObserver is unavailable.
     */
    _observePreload() {
      if (this._preloaded) return;
      if (!('IntersectionObserver' in window)) { this._preloadSlides(); return; }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this._preloadSlides();
          io.disconnect();
        }
      }, { rootMargin: '600px 0px' });
      io.observe(this);
    }

    _preloadSlides() {
      if (this._preloaded) return;
      this._preloaded = true;
      this.slides.forEach((slide) => {
        slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      });
    }

    buildDots() {
      if (!this.dotsWraps || !this.dotsWraps.length) return;
      // Build a parallel array of dot buttons per wrap so each wrap shows
      // all N dots; clicking any dot routes to that slide regardless of
      // which wrap it lives in.
      this.dotGroups = this.dotsWraps.map((wrap) => {
        wrap.innerHTML = '';
        const group = [];
        this.slides.forEach((_, i) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'salt-slider__dot';
          btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
          btn.addEventListener('click', () => this.go(i));
          wrap.appendChild(btn);
          group.push(btn);
        });
        return group;
      });
    }

    bindEvents() {
      this.prevBtns.forEach((btn) => btn.addEventListener('click', () => this.go(this.currentIndex - 1)));
      this.nextBtns.forEach((btn) => btn.addEventListener('click', () => this.go(this.currentIndex + 1)));

      // Swipe — only intercept when we're in transform mode. On mobile
      // "peek" layouts the viewport is natively scrollable; the browser
      // handles the swipe + scroll-snap, and a JS-driven `go()` here just
      // fights it (the smooth scrollTo races the native snap and leaves the
      // active dot out of sync). When scrollable, we register a scroll
      // listener below instead and let native gestures drive everything.
      let startX = null;
      this.track.addEventListener('touchstart', (e) => {
        if (this._isScrollable()) { startX = null; return; }
        startX = e.touches[0].clientX;
      }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) {
          this.go(this.currentIndex + (dx < 0 ? 1 : -1));
        }
        startX = null;
      });

      // Native scroll → active dot sync. When the user swipes in a
      // scrollable viewport, the browser moves to the snapped slide but
      // there's no JS callback by default, so the pagination dots stay on
      // the old slide and the slider feels broken. Watch scroll position
      // (debounced to the post-snap resting frame) and update state to
      // match whichever slide is closest to the current scrollLeft.
      const viewport = this.track.parentElement;
      if (viewport) {
        let scrollRaf = null;
        viewport.addEventListener('scroll', () => {
          if (!this._isScrollable()) return;
          if (scrollRaf) cancelAnimationFrame(scrollRaf);
          scrollRaf = requestAnimationFrame(() => {
            scrollRaf = null;
            const idx = this._activeIndexFromScroll();
            if (idx !== this.currentIndex) {
              this.currentIndex = idx;
              this._syncState();
            }
          });
        }, { passive: true });
      }

      // Keyboard
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.currentIndex + 1); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this.go(this.currentIndex - 1); }
      });

      // Per-slide height sync (transform-mode only). Viewport reflows when
      // the window resizes, and slide heights can grow once late-loading
      // images report their dimensions. Re-measure on both so the section
      // below the slider keeps a 64px gap from whichever card is showing.
      window.addEventListener('resize', () => this._syncHeight());
      this.slides.forEach((slide) => {
        slide.querySelectorAll('img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', () => this._syncHeight(), { once: true });
        });
      });
    }

    _isScrollable() {
      const viewport = this.track && this.track.parentElement;
      if (!viewport) return false;
      // Treat the viewport as a "native scroll" carousel based on its
      // configured overflow-x, NOT on current overflow state. Some
      // variants (science-slider) use `overflow-x: clip` and rely on
      // JS transforms — those return false here and use translateX as
      // expected. Others (how-to-use on mobile) declare `overflow-x:
      // auto` and intentionally let the browser handle scrolling +
      // scroll-snap natively. Using a scrollWidth > clientWidth check
      // raced with initial layout: on first load the flex slides
      // hadn't yet laid out as overflowing, so this returned false,
      // `_syncHeight` locked the viewport to the first slide's
      // pre-settle height, and the card bottom got cropped once
      // fonts/images finished resizing it. CSS intent is the stable
      // signal — use that instead.
      const overflowX = getComputedStyle(viewport).overflowX;
      return overflowX === 'auto' || overflowX === 'scroll';
    }

    _activeIndexFromScroll() {
      const viewport = this.track.parentElement;
      if (!viewport || !this.slides.length) return this.currentIndex;
      const scrollLeft = viewport.scrollLeft;
      let closest = 0;
      let minDist = Infinity;
      for (let i = 0; i < this.slides.length; i++) {
        const dist = Math.abs((this.slides[i].offsetLeft - this.track.offsetLeft) - scrollLeft);
        if (dist < minDist) { minDist = dist; closest = i; }
      }
      return closest;
    }

    _syncState() {
      this.slides.forEach((s, i) => {
        s.setAttribute('aria-hidden', String(i !== this.currentIndex));
      });
      if (this.dotGroups) {
        this.dotGroups.forEach((group) => {
          group.forEach((d, i) => d.classList.toggle('salt-slider__dot--active', i === this.currentIndex));
        });
      }
      this.dispatchEvent(new CustomEvent('saltslider:change', { detail: { index: this.currentIndex } }));
    }

    /**
     * Pull the viewport's height to match the active slide's natural
     * height. Lets the section grow/shrink with each slide so a short
     * slide (slide 1 of the science slider) stays tight against the
     * section below, and a taller slide (slide 2 with the bullet list)
     * pushes that section down — keeping the same 64px gap regardless.
     *
     * Skipped for native-scroll viewports (the how-to-use carousel),
     * where the browser already handles slide flow inside a scrollable
     * container and JS-managed height would fight scroll-snap.
     */
    _syncHeight() {
      const viewport = this.track && this.track.parentElement;
      if (!viewport) return;
      // Native-scroll viewports size to their content via the browser;
      // clear any inline height in case a prior call (under the old
      // scrollWidth race) locked one in, which would crop the slide
      // bottom once fonts/images finished resizing the card.
      if (this._isScrollable()) {
        if (viewport.style.height) viewport.style.height = '';
        return;
      }
      if (this._syncRaf) cancelAnimationFrame(this._syncRaf);
      this._syncRaf = requestAnimationFrame(() => {
        this._syncRaf = null;
        const active = this.slides && this.slides[this.currentIndex];
        if (!active) return;
        const h = active.offsetHeight;
        if (h > 0) viewport.style.height = h + 'px';
      });
    }

    go(idx) {
      const n = this.slides.length;
      this.currentIndex = ((idx % n) + n) % n;

      // If the track lives inside a horizontally scrolling viewport (mobile
      // peek carousel), scroll it into view instead of translating — the CSS
      // override blanks the transform on those breakpoints.
      if (this._isScrollable()) {
        const viewport = this.track.parentElement;
        const slide = this.slides[this.currentIndex];
        if (slide) {
          const target = slide.offsetLeft - this.track.offsetLeft;
          viewport.scrollTo({ left: target, behavior: 'smooth' });
        }
      } else {
        this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;
      }

      this._syncState();
      this._syncHeight();
    }
  }

  customElements.define('salt-slider', SaltSlider);

  // Backup: after the page fully loads, force-init any sliders that didn't
  // pick up children (covers cases where the upgrade fired before the
  // section's children were attached — e.g. Shopify section render API).
  const ensureAllInitialized = () => {
    document.querySelectorAll('salt-slider').forEach((el) => {
      if (!el._initialized && typeof el._init === 'function') {
        el._scheduled = false;
        el._retried = false;
        el._init();
      }
    });
  };
  if (document.readyState === 'complete') {
    requestAnimationFrame(ensureAllInitialized);
  } else {
    window.addEventListener('load', ensureAllInitialized, { once: true });
  }
})();
